import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiUrl } from '../lib/api';

/**
 * PUBLIC document verification — where a receipt's QR code lands.
 *
 * ⚠ NO LOGIN, and that is the entire point. The people who most need to check a document — a bank
 * asking for proof of fees paid, an employer checking a certificate, a parent making sure the slip
 * they were handed is real — are exactly the people with no Eduvora account. Requiring a sign-in
 * would make the QR decorative.
 *
 * ⚠ It also renders NO CHROME: no sidebar, no tenant nav, no sign-in prompt. Someone arriving here
 * from a phone camera has no idea what Eduvora is and does not need to. They asked one question and
 * get one answer.
 */

interface VerificationResult {
  found: boolean;
  documentType: string | null;
  reference: string | null;
  issuedTo: string | null;
  issuedOn: string | null;
  amount: string | null;
  issuedBy: string | null;
  isVoided: boolean;
  message: string;
}

export function VerifyPage() {
  const { kind, ref } = useParams<{ kind?: string; ref?: string }>();
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // ⚠ No code in the URL is NOT an error — it is somebody who typed the short address printed
    // on the receipt because they couldn't scan. They get an explanation, not a failure.
    if (!ref) { setLoading(false); return; }

    // Default to a payment: the receipt QR is by far the common case, and its URL is
    // /verify/{id} with no kind segment.
    const path = kind === 'certificate'
      ? `/api/verify/certificate/${encodeURIComponent(ref)}`
      : `/api/verify/payment/${encodeURIComponent(ref)}`;

    // ⚠ Plain fetch with NO auth header and NO tenant header. The endpoint is anonymous and
    // cross-tenant by design — a scanner has neither a token nor a subdomain.
    fetch(apiUrl(path))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then(setResult)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [kind, ref]);

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center',
      padding: 20, background: '#f5f4f0',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <div style={{
        width: '100%', maxWidth: 420, background: '#fff', borderRadius: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,.08), 0 8px 24px rgba(0,0,0,.06)',
        overflow: 'hidden',
      }}>
        {loading && (
          <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Checking…</div>
        )}

        {/* Arrived with no code — explain what to do. */}
        {!loading && !ref && (
          <>
            <Panel tone="unknown" title="Check a school document">
              Scan the QR code printed on your receipt or certificate with your phone camera — it
              opens this page and confirms the document straight away.
            </Panel>
            <div style={{ padding: '18px 22px 22px', fontSize: 13.5, color: '#555', lineHeight: 1.65 }}>
              {/* ⚠ Deliberately NOT a "enter your code" box. The code is a 32-character id that is
                  no longer printed on the paper, so a form asking for it would be a dead end
                  dressed up as help. The school office is the correct next step. */}
              If you can’t scan the code, contact the school office with the receipt number printed
              at the top of your slip — they can confirm it for you.
            </div>
          </>
        )}

        {!loading && ref && (failed || !result) && (
          <Panel tone="unknown" title="Couldn’t check this document">
            We couldn’t reach the school’s records just now. Please try again, or contact the school
            office directly.
          </Panel>
        )}

        {!loading && ref && result && !result.found && (
          <Panel tone="bad" title="No match found">
            {result.message}
          </Panel>
        )}

        {!loading && ref && result?.found && (
          <>
            <Panel
              tone={result.isVoided ? 'bad' : 'good'}
              title={result.isVoided ? 'Cancelled' : 'Genuine document'}
            >
              {result.message}
            </Panel>

            <div style={{ padding: '4px 22px 22px' }}>
              <Row label="Issued by" value={result.issuedBy} />
              <Row label="Document" value={result.documentType} />
              <Row label="Reference" value={result.reference} mono />
              <Row label="Issued to" value={result.issuedTo} />
              <Row label="Date" value={result.issuedOn} />
              {result.amount && <Row label="Amount" value={result.amount} />}

              {/* ⚠ Says explicitly what has and has NOT been checked. Someone holding a slip needs
                  to know this confirms the school issued it — not that the paper in their hand is
                  the original rather than a photocopy. Overstating it would be worse than useless. */}
              <p style={{ fontSize: 11.5, color: '#777', lineHeight: 1.6, marginTop: 18, marginBottom: 0 }}>
                This confirms the school’s own records match the document. The name is shortened for
                privacy. If anything here differs from the paper you’re holding, contact the school
                office.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Panel({ tone, title, children }: {
  tone: 'good' | 'bad' | 'unknown'; title: string; children: React.ReactNode;
}) {
  const bg = tone === 'good' ? '#0f7b48' : tone === 'bad' ? '#b3261e' : '#5b5b5b';
  return (
    <div style={{ background: bg, color: '#fff', padding: '22px 22px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Mark tone={tone} />
        <strong style={{ fontSize: 17 }}>{title}</strong>
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, opacity: 0.95 }}>{children}</div>
    </div>
  );
}

/** A tick or a cross — readable at a glance, and before any text is read. */
function Mark({ tone }: { tone: 'good' | 'bad' | 'unknown' }) {
  return (
    <span style={{
      width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,.22)',
      display: 'grid', placeItems: 'center', flex: 'none',
    }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"
        strokeLinecap="round" strokeLinejoin="round">
        {tone === 'good' ? <path d="M20 6 9 17l-5-5" />
          : tone === 'bad' ? <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>
            : <><path d="M12 8v5" /><path d="M12 17h.01" /></>}
      </svg>
    </span>
  );
}

function Row({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 14,
      padding: '9px 0', borderBottom: '1px solid #eee', fontSize: 13.5,
    }}>
      <span style={{ color: '#777', flex: 'none' }}>{label}</span>
      <span style={{
        fontWeight: 600, textAlign: 'right',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
        fontSize: mono ? 12.5 : undefined,
      }}>{value}</span>
    </div>
  );
}
